import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, RefreshCw, Plus, Pencil } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api, ApiError } from '../../lib/api';
import FilterChip from '../../components/crm/FilterChip';
import EmptyState from '../../components/crm/EmptyState';
import PaginatedTable from '../../components/admin/PaginatedTable';
import DomainModal from '../../components/admin/DomainModal';
import usePaginatedQuery from '../../hooks/usePaginatedQuery';
import SiteTransfersPanel from '../../components/admin/SiteTransfersPanel';

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = Date.parse(dateStr);
  if (!d) return null;
  return Math.floor((d - Date.now()) / 86400000);
}

function renewalTone(days) {
  if (days == null) return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  if (days < 0) return 'bg-red-500/15 text-red-300 border-red-500/25';
  if (days <= 7) return 'bg-red-500/10 text-red-300 border-red-500/20';
  if (days <= 30) return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
  return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20';
}

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20';
  if (s === 'pending' || s === 'initializing') return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
  if (!s) return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  return 'bg-white/[0.04] text-gray-300 border-white/[0.1]';
}

export default function Domains() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const expiringOnly = searchParams.get('expiring') === 'true';
  const ownership = searchParams.get('ownership') || ''; // '' | 'managed' | 'external'

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // domain row being edited
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'ok'|'err', text }

  const updateParams = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '' || v === false) next.delete(k);
        else next.set(k, String(v));
      }
      next.delete('offset');
      return next;
    });
  };
  const setExpiringOnly = (on) => updateParams({
    expiring: on ? 'true' : null,
    window_days: on ? '30' : null,
  });

  const paginator = usePaginatedQuery({
    path: '/api/admin/domains',
    responseKey: 'domains',
    extraParams: {
      expiring: expiringOnly ? 'true' : '',
      window_days: expiringOnly ? '30' : '',
      ownership,
    },
  });

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') return <EmptyState title="Staff only" />;

  const anyFilterSet = expiringOnly || !!ownership;

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setNotice(null);
    try {
      const r = await api.post('/api/admin/domains/sync', {});
      setNotice({ type: 'ok', text: r?.message || 'Sync complete.' });
      paginator.refetch();
    } catch (err) {
      setNotice({ type: 'err', text: err instanceof ApiError ? err.message : 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Domains</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Registrar + DNS + cutover tracking. Cloudflare zones linked to an account sync in automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={runSync}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] text-gray-300 border border-white/[0.1] hover:border-white/[0.25] hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync from Cloudflare'}
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E]"
          >
            <Plus className="w-3.5 h-3.5" /> Add domain
          </button>
        </div>
      </div>

      {notice && (
        <div className={`mb-4 px-3 py-2 rounded-lg text-[12px] border ${
          notice.type === 'ok'
            ? 'bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-200'
            : 'bg-red-500/10 border-red-500/20 text-red-300'
        }`}>
          {notice.text}
        </div>
      )}

      {/* Sites moving to a new address; finishing one stamps the domain's cutover date. */}
      <SiteTransfersPanel onTransferred={() => paginator.refetch()} />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterChip active={!anyFilterSet} onClick={() => updateParams({ expiring: null, window_days: null, ownership: null })}>All</FilterChip>
        <FilterChip active={expiringOnly} onClick={() => setExpiringOnly(true)}>Expiring ≤30 days</FilterChip>
        {/* Sprint I M5: NexSav-managed vs External chips per plan §4.5. */}
        <FilterChip active={ownership === 'managed'} onClick={() => updateParams({ ownership: 'managed' })}>NexSav-managed</FilterChip>
        <FilterChip active={ownership === 'external'} onClick={() => updateParams({ ownership: 'external' })}>External</FilterChip>
      </div>
      <PaginatedTable
        paginator={paginator}
        anyFilterSet={anyFilterSet}
        emptyMessage="No domains yet. Link a Cloudflare zone to a school (it syncs in here) or add one manually."
        emptyFilteredMessage="No domains match these filters."
        renderTable={(rows) => (
          <div className="rounded-xl border border-white/[0.05] bg-white/[0.01] overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-white/[0.02] border-b border-white/[0.05] text-[10px] uppercase tracking-wider text-gray-500">
              <div className="col-span-7 lg:col-span-3">Domain</div>
              <div className="col-span-3 lg:col-span-2">Status</div>
              <div className="hidden lg:block lg:col-span-2">Registrar</div>
              <div className="hidden lg:block lg:col-span-2">Renewal</div>
              <div className="hidden lg:block lg:col-span-2">Cutover</div>
              <div className="col-span-2 lg:col-span-1 text-right">Edit</div>
            </div>
            {rows.map((d) => {
              // Renewal prefers the registrar date (manual) but falls back to the
              // Cloudflare zone expiry so synced rows aren't blank.
              const renewalAt = d.registrarRenewalAt || d.expiresAt;
              const days = daysUntil(renewalAt);
              // Status prefers the live CF zone status, falls back to manual SSL.
              const status = d.cfStatus || d.sslStatus;
              return (
                <div key={d.id} className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-white/[0.03] text-[12px] items-center">
                  <div className="col-span-7 lg:col-span-3 text-white font-mono truncate">
                    <a href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 hover:text-[#B0FC38]">
                      {d.domain} <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  </div>
                  <div className="col-span-3 lg:col-span-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${statusTone(status)}`}>
                      {status || '—'}
                    </span>
                  </div>
                  <div className="hidden lg:block lg:col-span-2 text-gray-400">{d.registrar || (d.cfZoneId ? 'Cloudflare' : '—')}</div>
                  <div className="hidden lg:block lg:col-span-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${renewalTone(days)}`}>
                      {renewalAt ? `${String(renewalAt).slice(0, 10)} (${days}d)` : '—'}
                    </span>
                  </div>
                  <div className="hidden lg:block lg:col-span-2 text-gray-400">{d.cutoverCompletedAt ? String(d.cutoverCompletedAt).slice(0, 10) : '—'}</div>
                  <div className="col-span-2 lg:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setEditing(d)}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06]"
                      aria-label={`Edit ${d.domain}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      />

      {showAdd && (
        <DomainModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setNotice({ type: 'ok', text: 'Domain added.' }); paginator.refetch(); }}
        />
      )}
      {editing && (
        <DomainModal
          domain={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setNotice({ type: 'ok', text: 'Domain updated.' }); paginator.refetch(); }}
        />
      )}
    </div>
  );
}
